import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import {
  ComposedChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import LotteryWeekSelector, { getInitialWeekStart, getWeekRange } from '../../../components/LotteryWeekSelector';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import FilterSelectorWithApp from '../../../components/FilterSelectorWithApp';
import apiClient from '../../../utils/axiosClient';
import type {
  EnterCountResponse,
  EnterDistributionItem,
  EnterAmountDistributionItem,
} from '../../../types/luckyLottery.types';

dayjs.locale('ko');

// ────────────────────────────────────────
// 상수
// ────────────────────────────────────────

const STORAGE_KEY = 'avatye.ops.lucky-lottery.enter.filter';

/** 히트맵 색상 (낮음 → 높음) */
const HEATMAP_COLORS = [
  '#f8fafc',
  '#dbeafe',
  '#93c5fd',
  '#3b82f6',
  '#1d4ed8',
  '#1e3a8a',
];

// ────────────────────────────────────────
// 필터 영속화 헬퍼
// ────────────────────────────────────────

type StoredFilter = {
  startDate: string;  // 'YYYY-MM-DD' (주의 일요일)
  endDate: string;    // 'YYYY-MM-DD' (주의 토요일)
  appName: string;
};

const getStoredFilter = (): StoredFilter | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as StoredFilter;
  } catch {
    // ignore
  }
  return null;
};

const saveFilter = (filter: StoredFilter) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filter));
  } catch {
    // ignore
  }
};

// ────────────────────────────────────────
// 히트맵 색상 계산 헬퍼
// ────────────────────────────────────────

const getHeatmapColor = (value: number, maxValue: number): string => {
  if (maxValue === 0 || value === 0) return HEATMAP_COLORS[0];
  const ratio = value / maxValue;
  const idx = Math.min(
    Math.floor(ratio * (HEATMAP_COLORS.length - 1)),
    HEATMAP_COLORS.length - 1,
  );
  return HEATMAP_COLORS[idx];
};

const getHeatmapTextColor = (value: number, maxValue: number): string => {
  if (maxValue === 0 || value === 0) return '#94a3b8';
  const ratio = value / maxValue;
  return ratio >= 0.6 ? '#ffffff' : '#1e293b';
};

// ────────────────────────────────────────
// 커스텀 툴팁
// ────────────────────────────────────────

type TooltipPayloadItem = {
  name: string;
  value: number;
  color: string;
};

function CustomLineTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 1.5,
        p: 1.5,
        boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
        minWidth: 160,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#1e293b' }}>
        {label}
      </Typography>
      {payload.map((item) => (
        <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: item.color,
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: '0.78rem', color: '#374151' }}>
            {item.name}:{' '}
            <strong>{Number(item.value).toLocaleString('ko-KR')}</strong>
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ────────────────────────────────────────
// BoxPlot 커스텀 Shape
// ────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BoxPlotShape = (props: any) => {
  const { x, y, width, height, payload } = props as {
    x: number; y: number; width: number; height: number;
    payload: EnterAmountDistributionItem & { date: string };
  };

  if (!payload || !height || height <= 0) return null;
  const { minVal, q1, median, q3, maxVal, mean } = payload;
  if (maxVal === 0) return null;

  const getY = (v: number) => y + height * (1 - v / maxVal);

  const cx = x + width / 2;
  const boxW = Math.max(width * 0.55, 14);
  const boxX = cx - boxW / 2;
  const capW = boxW * 0.45;

  const yMin = getY(minVal);
  const yQ1 = getY(q1);
  const yMedian = getY(median);
  const yQ3 = getY(q3);
  const yMax = getY(maxVal);
  const yMean = getY(mean);

  return (
    <g>
      <line x1={cx} y1={yMin} x2={cx} y2={yMax} stroke="#94a3b8" strokeWidth={1.5} />
      <line x1={cx - capW / 2} y1={yMin} x2={cx + capW / 2} y2={yMin} stroke="#64748b" strokeWidth={1.5} />
      <line x1={cx - capW / 2} y1={yMax} x2={cx + capW / 2} y2={yMax} stroke="#64748b" strokeWidth={1.5} />
      <rect x={boxX} y={yQ3} width={boxW} height={Math.max(yQ1 - yQ3, 1)} fill="#dbeafe" stroke="#3b82f6" strokeWidth={1.5} rx={2} />
      <line x1={boxX} y1={yMedian} x2={boxX + boxW} y2={yMedian} stroke="#1d4ed8" strokeWidth={2.5} />
      <circle cx={cx} cy={yMean} r={3.5} fill="#ef4444" stroke="#fff" strokeWidth={1} />
    </g>
  );
};

function BoxPlotTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload as EnterAmountDistributionItem;
  if (!d) return null;

  const rows = [
    { label: '최댓값', value: d.maxVal, color: '#64748b' },
    { label: 'Q3 (75%)', value: d.q3, color: '#3b82f6' },
    { label: '중앙값', value: d.median, color: '#1d4ed8' },
    { label: '평균', value: d.mean, color: '#ef4444' },
    { label: 'Q1 (25%)', value: d.q1, color: '#3b82f6' },
    { label: '최솟값', value: d.minVal, color: '#64748b' },
  ];

  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 1.5, p: 1.5, boxShadow: '0 2px 8px rgba(0,0,0,0.10)', minWidth: 170 }}>
      <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.8, color: '#1e293b' }}>{label}</Typography>
      {rows.map(({ label: rowLabel, value, color }) => (
        <Box key={rowLabel} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.25 }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>{rowLabel}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color, fontWeight: 600 }}>
            {Math.round(value).toLocaleString('ko-KR')}장
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ────────────────────────────────────────
// 시간대 그룹 정의 (차트용)
// ────────────────────────────────────────

const HOUR_GROUPS = [
  { label: '새벽(0~5시)', hours: [0, 1, 2, 3, 4, 5], color: '#6366f1' },
  { label: '오전(6~11시)', hours: [6, 7, 8, 9, 10, 11], color: '#0044D2' },
  { label: '오후(12~17시)', hours: [12, 13, 14, 15, 16, 17], color: '#f59e0b' },
  { label: '저녁(18~23시)', hours: [18, 19, 20, 21, 22, 23], color: '#ef4444' },
];

// ────────────────────────────────────────
// 페이지 컴포넌트
// ────────────────────────────────────────

export default function EnterCount() {
  // ── 필터 초기값 (localStorage 복원)
  const stored = useMemo(() => getStoredFilter(), []);

  const initialWeek = useMemo(() => {
    if (stored?.startDate && stored?.endDate) {
      return { startDate: stored.startDate, endDate: stored.endDate };
    }
    const sunday = getInitialWeekStart();
    const { start, end } = getWeekRange(sunday);
    return { startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD') };
  }, [stored]);

  const [startDate, setStartDate] = useState<string>(initialWeek.startDate);
  const [endDate, setEndDate] = useState<string>(initialWeek.endDate);
  const [appName, setAppName] = useState<string>(stored?.appName ?? '앱 전체');
  const [selectedOs, setSelectedOs] = useState<string>('all');

  // ── 앱 목록
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);

  // ── 데이터 상태
  const [data, setData] = useState<EnterCountResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // ── 앱 목록 조회
  const fetchAppOptions = useCallback(() => {
    if (!startDate || !endDate) return;
    apiClient
      .get<string[]>('/api/v1/stat/lucky-lottery/app/list', { params: { start_date: startDate, end_date: endDate } })
      .then((res) => {
        setAppOptions(['앱 전체', ...(res.data || [])]);
      })
      .catch(() => undefined);
  }, [startDate, endDate]);

  // ── 데이터 조회
  const fetchData = useCallback(() => {
    if (!startDate || !endDate) return;

    const app = appName === '앱 전체' ? null : appName;

    // 필터 저장
    saveFilter({ startDate, endDate, appName });

    setLoading(true);
    apiClient
      .get<EnterCountResponse>('/api/v1/analytics/lucky-lottery/ticket/enter', {
        params: {
          start_date: startDate,
          end_date: endDate,
          ...(app ? { app_name: app } : {}),
          ...(selectedOs !== 'all' ? { os_type: selectedOs } : {}),
        },
      })
      .then((res) => {
        setData(res.data);
        setErrorMessage('');
      })
      .catch(() => {
        setData(null);
        setErrorMessage('데이터 로드에 실패했습니다.');
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, appName, selectedOs]);

  useEffect(() => {
    fetchAppOptions();
  }, [fetchAppOptions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── distribution 에서 날짜 목록 추출
  const dates = useMemo((): string[] => {
    if (!data?.distribution?.length) return [];
    return Array.from(new Set(data.distribution.map((d) => d.date))).sort();
  }, [data]);

  // ── 차트 데이터 가공: 날짜 × 시간대 그룹별 합산
  const chartData = useMemo(() => {
    if (!data?.distribution?.length || !dates.length) return [];

    return dates.map((date) => {
      const dayItems: EnterDistributionItem[] = data.distribution.filter((d) => d.date === date);
      const row: Record<string, string | number> = { date };
      for (const group of HOUR_GROUPS) {
        row[group.label] = dayItems
          .filter((d) => group.hours.includes(d.hour))
          .reduce((acc, d) => acc + d.ticketSum, 0);
      }
      return row;
    });
  }, [data, dates]);

  // ── 히트맵 데이터: 날짜(행) × 시간(열) 피벗
  const heatmapRows = useMemo(() => {
    if (!data?.distribution?.length || !dates.length) return [];

    return dates.map((date) => {
      const hours: Record<number, number> = {};
      for (let h = 0; h < 24; h++) {
        const found = data.distribution.find((d) => d.date === date && d.hour === h);
        hours[h] = found ? found.ticketSum : 0;
      }
      return { date, hours };
    });
  }, [data, dates]);

  // ── 히트맵 최대값
  const heatmapMax = useMemo(() => {
    if (!data?.distribution?.length) return 0;
    return Math.max(...data.distribution.map((d) => d.ticketSum));
  }, [data]);

  // ── BoxPlot 데이터
  const boxPlotData = useMemo(() => {
    if (!data?.amountDistribution?.length) return [];
    return data.amountDistribution.map((item) => ({
      ...item,
      date: dayjs(item.date).format('MM/DD'),
    }));
  }, [data]);

  return (
    <Box>
      {/* ── 상단 필터 영역 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 1.5, md: 0 },
        }}
      >
        <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
          <LotteryWeekSelector
            startDate={startDate}
            endDate={endDate}
            onChange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
        </Box>

        <Box sx={{ ml: { xs: 0, md: 3 }, mt: { xs: 1.5, md: 0 }, width: { xs: '100%', md: 'auto' } }}>
          <FilterSelectorWithApp
            selectedApp={appName}
            appOptions={appOptions}
            onAppChange={(v) => setAppName(v ?? '앱 전체')}
            onSearch={fetchData}
            selectedOs={selectedOs}
            onOsChange={setSelectedOs}
          />
        </Box>
      </Box>

      {/* ── 본문 카드 */}
      <Box
        sx={PAGE_CARD_SX}
      >
        {/* 앱 필터 Chip */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setAppName(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: appName === name ? 700 : 400,
                backgroundColor: appName === name ? '#0044D2' : '#fff',
                color: appName === name ? '#fff' : '#374151',
                border: `1px solid ${appName === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': {
                  backgroundColor: appName === name ? '#0033a0' : '#f3f4f6',
                },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider sx={{ borderColor: '#f1f5f9', mb: 3 }} />

        {/* ── 로딩 / 에러 / 콘텐츠 분기 */}
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={400}
            gap={2}
          >
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1">데이터 로딩 중...</Typography>
          </Box>
        ) : errorMessage ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={400}
          >
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : (
          <>
            {/* ── 요약 카드 2개 */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2,
                mb: 4,
              }}
            >
              {/* 총 응모권 소비량 */}
              <Box
                sx={{
                  backgroundColor: '#f8fafc',
                  borderRadius: 3,
                  p: 2.5,
                  border: '1px solid #f1f5f9',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <Typography sx={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 500 }}>
                    총 응모권 소비량
                  </Typography>
                  <Tooltip title="조회 기간 내 전체 응모에 사용된 응모권 합계입니다." arrow placement="right">
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: '#9ca3af', cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
                <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, color: '#0044D2', lineHeight: 1.2 }}>
                  {data ? data.totalTicketSum.toLocaleString('ko-KR') : '—'}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>
                  장
                </Typography>
              </Box>

              {/* 1인당 평균 응모 수량 */}
              <Box
                sx={{
                  backgroundColor: '#f8fafc',
                  borderRadius: 3,
                  p: 2.5,
                  border: '1px solid #f1f5f9',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <Typography sx={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 500 }}>
                    1인당 평균 응모 수량
                  </Typography>
                  <Tooltip title="조회 기간 내 응모 유저 1인당 평균 응모 횟수입니다." arrow placement="right">
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: '#9ca3af', cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
                <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
                  {data ? data.avgTicketPerUser.toFixed(2) : '—'}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>
                  장 / 명
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ borderColor: '#f1f5f9', mb: 3 }} />

            {/* ── BoxPlot 섹션: 일별 1인당 응모 수량 분포 */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                  일별 1인당 응모 수량 분포
                </Typography>
                <Tooltip
                  title="유저 1인당 일별 응모 수량의 분포입니다. 상자는 Q1~Q3(IQR 구간), 진한 선은 중앙값, 빨간 점은 평균, 수염은 최솟~최댓값입니다."
                  arrow
                  placement="right"
                >
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: '#9ca3af', cursor: 'pointer' }} />
                </Tooltip>
              </Box>

              {/* 범례 */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                {[
                  { type: 'rect', color: '#3b82f6', label: 'IQR 박스 (Q1~Q3)' },
                  { type: 'line', color: '#1d4ed8', label: '중앙값' },
                  { type: 'dot',  color: '#ef4444', label: '평균' },
                  { type: 'line', color: '#94a3b8', label: '최솟~최댓값 수염' },
                ].map(({ type, color, label }) => (
                  <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    {type === 'rect' && (
                      <Box sx={{ width: 14, height: 10, bgcolor: '#dbeafe', border: `2px solid ${color}`, borderRadius: 0.5 }} />
                    )}
                    {type === 'line' && (
                      <Box sx={{ width: 14, height: 2.5, bgcolor: color, borderRadius: 0.5 }} />
                    )}
                    {type === 'dot' && (
                      <Box sx={{ width: 9, height: 9, bgcolor: color, borderRadius: '50%' }} />
                    )}
                    <Typography sx={{ fontSize: '0.78rem', color: '#374151' }}>{label}</Typography>
                  </Box>
                ))}
              </Box>

              {boxPlotData.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={160}>
                  <Typography color="#94a3b8">데이터가 없습니다.</Typography>
                </Box>
              ) : (
                <Box sx={{ height: 180, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={boxPlotData} margin={{ top: 10, right: 24, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="0" stroke="#f3f4f6" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: '#6b7280', fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fill: '#6b7280', fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => Number(v).toLocaleString('ko-KR')}
                        width={56}
                      />
                      <RechartsTooltip content={<BoxPlotTooltip />} cursor={{ fill: 'rgba(0,68,210,0.04)' }} />
                      <Bar dataKey="maxVal" shape={<BoxPlotShape />} isAnimationActive={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Box>

            <Divider sx={{ borderColor: '#f1f5f9', mb: 3 }} />

            {/* ── 차트 섹션: 시간대 그룹별 일별 응모 추이 */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                  시간대별 일일 응모 추이
                </Typography>
                <Tooltip
                  title="새벽(0~5시) / 오전(6~11시) / 오후(12~17시) / 저녁(18~23시) 4개 구간으로 집계한 일별 응모권 소비량입니다."
                  arrow
                  placement="right"
                >
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: '#9ca3af', cursor: 'pointer' }} />
                </Tooltip>
              </Box>

              {chartData.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <Typography color="#94a3b8">데이터가 없습니다.</Typography>
                </Box>
              ) : (
                <>
                  {/* 범례 */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1.5 }}>
                    {HOUR_GROUPS.map((g) => (
                      <Box key={g.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: g.color }} />
                        <Typography sx={{ fontSize: '0.78rem', color: '#374151' }}>{g.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ height: 320, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={chartData}
                        margin={{ top: 10, right: 24, left: 8, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="0" stroke="#f3f4f6" vertical={false} />
                        <XAxis
                          dataKey="date"
                          tick={{ fill: '#6b7280', fontSize: 11 }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(v) => dayjs(v).format('MM/DD')}
                        />
                        <YAxis
                          tick={{ fill: '#6b7280', fontSize: 11 }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(v) => Number(v).toLocaleString('ko-KR')}
                          width={56}
                        />
                        <RechartsTooltip
                          content={<CustomLineTooltip />}
                          cursor={{ stroke: '#e5e7eb' }}
                        />
                        {HOUR_GROUPS.map((group) => (
                          <Line
                            key={group.label}
                            type="monotone"
                            dataKey={group.label}
                            name={group.label}
                            stroke={group.color}
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </>
              )}
            </Box>

            <Divider sx={{ borderColor: '#f1f5f9', mb: 3 }} />

            {/* ── 히트맵 섹션: 날짜 × 시간대 응모 분포 */}
            <Box sx={{ pb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                  일별 시간대 응모 분포 히트맵
                </Typography>
                <Tooltip
                  title="날짜별 0~23시 응모권 소비량을 색상 농도로 표현합니다. 진할수록 응모량이 많습니다."
                  arrow
                  placement="right"
                >
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: '#9ca3af', cursor: 'pointer' }} />
                </Tooltip>
              </Box>

              {/* 색상 범례 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>적음</Typography>
                {HEATMAP_COLORS.map((c) => (
                  <Box key={c} sx={{ width: 20, height: 14, bgcolor: c, border: '1px solid #e2e8f0', borderRadius: 0.5 }} />
                ))}
                <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>많음</Typography>
              </Box>

              {heatmapRows.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                  <Typography color="#94a3b8">데이터가 없습니다.</Typography>
                </Box>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ borderRadius: 0, border: '1px solid #f1f5f9' }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              bgcolor: '#f8fafc',
                              borderBottom: '2px solid #e2e8f0',
                              fontSize: '0.82rem',
                              color: '#475569',
                              minWidth: 90,
                              whiteSpace: 'nowrap',
                              position: 'sticky',
                              left: 0,
                              zIndex: 3,
                            }}
                          >
                            날짜
                          </TableCell>
                          {Array.from({ length: 24 }, (_, h) => (
                            <TableCell
                              key={h}
                              align="center"
                              sx={{
                                fontWeight: 700,
                                bgcolor: '#f8fafc',
                                borderBottom: '2px solid #e2e8f0',
                                fontSize: '0.72rem',
                                color: '#475569',
                                px: 0.5,
                                minWidth: 38,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {String(h).padStart(2, '0')}시
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {heatmapRows.map((row) => (
                          <TableRow key={row.date} hover>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.82rem',
                                color: '#1e293b',
                                whiteSpace: 'nowrap',
                                bgcolor: '#fff',
                                position: 'sticky',
                                left: 0,
                                zIndex: 1,
                                borderRight: '1px solid #e2e8f0',
                              }}
                            >
                              {row.date}
                            </TableCell>
                            {Array.from({ length: 24 }, (_, h) => {
                              const val = row.hours[h] ?? 0;
                              return (
                                <TableCell
                                  key={h}
                                  align="center"
                                  sx={{
                                    px: 0.5,
                                    py: 0.8,
                                    fontSize: '0.7rem',
                                    bgcolor: getHeatmapColor(val, heatmapMax),
                                    color: getHeatmapTextColor(val, heatmapMax),
                                    fontWeight: val > 0 ? 500 : 400,
                                    transition: 'background-color 0.15s ease',
                                    '&:hover': { opacity: 0.85 },
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={`${row.date} ${String(h).padStart(2, '0')}시: ${val.toLocaleString('ko-KR')}장`}
                                >
                                  {val > 0 ? val.toLocaleString('ko-KR') : ''}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
